import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetFeatureFlagAsync } from '@/features/featuresFlag/apis';
import { FEATURES_FLAG_KEYS } from '@/features/featuresFlag/apis/featureFlag.keys';
import { useGetReferentialsAsync, REFERENCIALS_URL } from '@/features/shared/apis';
import { useFolderTypologyState } from '@/features/shared/hooks';
import { GetFolderTypologyTypeReferentialResponse } from '@/features/shared/types';
import { useGetClientAccessAsync, useGetServicesOfferTypologiesAsync } from '@/features/sherpass/servicesOffer/apis';

export const useServicesOffer = () => {
  const navigate = useNavigate();
  const { setFolderTypologyId } = useFolderTypologyState();
  const [isOpenCreateNewRequestModal, setIsOpenCreateNewRequestModal] = useState(false);

  const { data: folderTypologiesResponse } = useGetReferentialsAsync<GetFolderTypologyTypeReferentialResponse>(
    REFERENCIALS_URL.FOLDER_TYPOLOGY,
  );

  const { data: clientAccess, isLoading: isGetClientAccessLoading } = useGetClientAccessAsync();

  const { data: isCommercialOfferAccessManagementEnabled, isLoading: isCommercialOfferAccessManagementLoading } =
    useGetFeatureFlagAsync({
      featureFlagKey: FEATURES_FLAG_KEYS.COMMERCIAL_OFFER_ACCESS_MANAGEMENT,
    });

  const {
    data: folderTypologiesByBorderCrossingServiceResponse = new Map<string, string>(),
    isLoading: isGetFolderTypologiesByBorderCrossingServiceResponseLoading,
  } = useGetServicesOfferTypologiesAsync();

  const getTypologyTranslationKey = (typologyId: string) =>
    (folderTypologiesResponse?.folderTypologyList ?? []).find((typo) => typo.id === typologyId)?.translationKey ?? '';

  const handleOpenCreateNewRequestModal = useCallback(
    (typology: string) => () => {
      setIsOpenCreateNewRequestModal(true);
      setFolderTypologyId(typology);
    },
    [setFolderTypologyId],
  );

  const handleCloseCreateNewRequestModal = useCallback(() => {
    setIsOpenCreateNewRequestModal(false);
    setFolderTypologyId(undefined);
  }, [setFolderTypologyId]);

  const handleCreateNewBorderCrossingRoroRequest = useCallback(() => navigate(`/new-request`), [navigate]);

  return {
    isGetFolderTypologiesByBorderCrossingServiceResponseLoading,
    folderTypologiesByBorderCrossingServiceResponse,
    isCommercialOfferAccessManagementEnabled,
    isCommercialOfferAccessManagementLoading,
    isOpenCreateNewRequestModal,
    isGetClientAccessLoading,
    clientAccess,
    getTypologyTranslationKey,
    handleOpenCreateNewRequestModal,
    handleCloseCreateNewRequestModal,
    handleCreateNewBorderCrossingRoroRequest,
  };
};
